import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../translations";
import { API_BASE } from '../config';
import {
  PanelContainer, PageHeader, Title, PanelCard, TitleRow,
  FieldLabel, TextInput, PrimaryButton, SecondaryButton, IconButton,
} from '../ui/common';
import { Banner } from '../components/ui/Banner';
import { CheckIcon, XIcon, UserIcon, MailIcon, ArrowLeftIcon } from '../ui/icons';
import { scrollToTop } from '../utils/scrollToTop';

// Ikony bez odpowiednika we wspólnym zestawie (aparat) — lokalne, currentColor,
// bez rozmiaru wpisanego na sztywno.
const PhoneIcon = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CameraIcon = (p) => (
  <svg width={p.size || 20} height={p.size || 20} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const ProfileSection = styled.div`
  display: grid;
  gap: var(--sp-8);
  grid-template-columns: 160px 1fr;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--sp-6);
    text-align: center;
  }
`;

const AvatarSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-4);
`;

const Avatar = styled.button`
  width: 120px;
  height: 120px;
  border-radius: var(--r-pill);
  overflow: hidden;
  background: var(--panel-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform var(--dur) var(--ease), box-shadow var(--dur) var(--ease);
  border: 4px solid var(--card-bg);
  box-shadow: var(--shadow-md);
  padding: 0;

  &:hover {
    transform: scale(1.05);
    box-shadow: var(--shadow-lg);
  }
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.div`
  color: var(--text-muted);
  font-size: var(--fs-sm);
  font-weight: 500;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-2);
`;

const AvatarButtons = styled.div`
  display: flex;
  gap: var(--sp-2);
  flex-wrap: wrap;
  justify-content: center;
`;

const AvatarButton = styled.button`
  background: ${(p) => (p.$danger ? 'var(--danger-bg)' : 'var(--info-bg)')};
  color: ${(p) => (p.$danger ? 'var(--danger)' : 'var(--info)')};
  border: 1px solid ${(p) => (p.$danger ? 'var(--danger-border)' : 'var(--info-border)')};
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--r-sm);
  font-size: var(--fs-xs);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--dur) var(--ease);
  display: inline-flex;
  align-items: center;
  gap: var(--sp-1);

  &:hover {
    filter: brightness(0.95);
    transform: translateY(-1px);
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--sp-5);
`;

const FormGrid = styled.div`
  display: grid;
  gap: var(--sp-5);

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormGroup = styled.div`
  &.full-width {
    grid-column: 1 / -1;
  }
`;

const LabelRow = styled(FieldLabel)`
  display: flex;
  align-items: center;
  gap: var(--sp-1);
`;

// TextInput z ui/common.js nie zna stanu błędu pola (błąd tam dostaje Banner, nie ramka
// inputu) — tu jest to jednak przydatny sygnał przy walidacji formularza, więc lekkie
// rozszerzenie na tokenach, a nie kopia całego inputu.
const FieldInput = styled(TextInput)`
  ${(p) => p.$invalid && `
    border-color: var(--danger);
    background: var(--danger-bg);
  `}
`;

const HiddenInput = styled.input`
  display: none;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: var(--sp-3);
  justify-content: flex-end;
  margin-top: var(--sp-8);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    justify-content: stretch;
  }
`;

const CenteredNote = styled.div`
  text-align: center;
  padding: var(--sp-8);
  color: var(--text-muted);
`;

export default function YourData() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storedUserId = localStorage.getItem('userId');
  const userId = storedUserId ? Number(storedUserId) : null;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [data, setData] = useState({
    firstname: "",
    lastname: "",
    email: "",
    phone_number: "",
    profile_picture: null
  });
  const [original, setOriginal] = useState(null);
  const fileRef = useRef(null);

  // Pobierz dane
  useEffect(() => {
    let abort = false;
    (async () => {
      if (userId == null) {
        setLoading(false);
        setError(t('yourData.noUser'));
        return;
      }
      try {
        const r = await fetch(`${API_BASE}/api/users/${userId}`, { credentials: 'include' });
        if (!r.ok) throw 0;
        const u = await r.json();
        if (!abort) {
          const norm = {
            firstname: u.firstname || "",
            lastname: u.lastname || "",
            email: u.email || "",
            phone_number: u.phone_number || "",
            profile_picture: u.profile_picture || null
          };
          setData(norm);
          setOriginal(norm);
        }
      } catch {
        setError(t('yourData.loadError'));
      } finally {
        if (!abort) setLoading(false);
      }
    })();
    return () => { abort = true; };
  }, [userId, t]);

  const changed = original && JSON.stringify(data) !== JSON.stringify(original);

  const onChange = (k, v) => {
    setData(d => ({ ...d, [k]: v }));
    setFieldErrors(prev => ({ ...prev, [k]: false }));
    setError('');
  };

  const onFile = e => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1024 * 1024) {
      setError(t('yourData.fileTooLarge'));
      setSuccess('');
      scrollToTop();
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      onChange('profile_picture', reader.result);
    };
    reader.readAsDataURL(f);
  };

  const reset = () => {
    if (original) setData(original);
    setError('');
    setSuccess('');
    setFieldErrors({});
  };

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    setFieldErrors({});

    const requiredErrors = {};
    if (!data.firstname.trim()) requiredErrors.firstname = true;
    if (!data.lastname.trim()) requiredErrors.lastname = true;
    if (Object.keys(requiredErrors).length) {
      setFieldErrors(requiredErrors);
      setError(t('yourData.fillRequired'));
      scrollToTop();
      setSaving(false);
      return;
    }

    try {
      const csrf = localStorage.getItem('csrfToken') || '';
      const r = await fetch(`${API_BASE}/api/users/${userId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify(data)
      });
      if (!r.ok) {
        const text = await r.text();
        const lower = (text || '').toLowerCase();
        const errors = {};
        if (/imi|first/.test(lower)) errors.firstname = true;
        if (/nazw|last/.test(lower)) errors.lastname = true;
        if (/email/.test(lower)) errors.email = true;
        if (/telefon|phone/.test(lower)) errors.phone_number = true;
        setFieldErrors(errors);
        setError(text || t('yourData.saveError'));
        scrollToTop();
        throw new Error(text || 'save-error');
      }
      setOriginal(data);
      setSuccess(t('yourData.saved'));
      setTimeout(() => setSuccess(''), 5000);
      scrollToTop();
    } catch (e) {
      if (!e?.message?.includes('save-error')) {
        setError(t('yourData.saveError'));
        scrollToTop();
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <PanelContainer>
      <CenteredNote>{t('common.loading')}</CenteredNote>
    </PanelContainer>
  );

  if (userId == null) return (
    <PanelContainer>
      <CenteredNote>{t('yourData.loginAgain')}</CenteredNote>
    </PanelContainer>
  );

  return (
    <PanelContainer>
      <PageHeader>
        <TitleRow>
          <IconButton onClick={() => navigate('/profile')} aria-label={t('common.backToProfile')}>
            <ArrowLeftIcon />
          </IconButton>
          <Title style={{ margin: 0 }}>{t('yourData.title')}</Title>
        </TitleRow>
      </PageHeader>

      {error && (
        <Banner tone="danger" icon={<XIcon size={20} />}>{error}</Banner>
      )}

      {success && (
        <Banner tone="success" icon={<CheckIcon size={20} />}>{success}</Banner>
      )}

      <PanelCard>
        <ProfileSection>
          <AvatarSection>
            <Avatar type="button" onClick={() => fileRef.current?.click()} aria-label={t('yourData.choose')}>
              {data.profile_picture ? (
                <AvatarImage src={data.profile_picture} alt="" />
              ) : (
                <AvatarPlaceholder>
                  <CameraIcon />
                  {t('yourData.noPhoto')}
                </AvatarPlaceholder>
              )}
            </Avatar>

            <AvatarButtons>
              <AvatarButton type="button" onClick={() => fileRef.current?.click()}>
                <CameraIcon size={14} />
                {t('yourData.choose')}
              </AvatarButton>
              {data.profile_picture && (
                <AvatarButton type="button" $danger onClick={() => onChange('profile_picture', null)}>
                  <XIcon size={14} />
                  {t('yourData.remove')}
                </AvatarButton>
              )}
            </AvatarButtons>

            <HiddenInput
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFile}
            />
          </AvatarSection>

          <FormSection>
            <FormGrid>
              <FormGroup>
                <LabelRow htmlFor="yourdata-firstname">
                  <UserIcon size={16} />
                  {t('yourData.firstName')}
                </LabelRow>
                <FieldInput
                  id="yourdata-firstname"
                  type="text"
                  placeholder={t('yourData.firstNamePlaceholder')}
                  value={data.firstname}
                  onChange={e => onChange('firstname', e.target.value)}
                  $invalid={!!fieldErrors.firstname}
                />
              </FormGroup>

              <FormGroup>
                <LabelRow htmlFor="yourdata-lastname">
                  <UserIcon size={16} />
                  {t('yourData.lastName')}
                </LabelRow>
                <FieldInput
                  id="yourdata-lastname"
                  type="text"
                  placeholder={t('yourData.lastNamePlaceholder')}
                  value={data.lastname}
                  onChange={e => onChange('lastname', e.target.value)}
                  $invalid={!!fieldErrors.lastname}
                />
              </FormGroup>

              <FormGroup className="full-width">
                <LabelRow htmlFor="yourdata-email">
                  <MailIcon size={16} />
                  {t('yourData.email')}
                </LabelRow>
                <FieldInput
                  id="yourdata-email"
                  type="email"
                  placeholder={t('yourData.emailPlaceholder')}
                  value={data.email}
                  onChange={e => onChange('email', e.target.value)}
                  $invalid={!!fieldErrors.email}
                />
              </FormGroup>

              <FormGroup className="full-width">
                <LabelRow htmlFor="yourdata-phone">
                  <PhoneIcon size={16} />
                  {t('yourData.phone')}
                </LabelRow>
                <TextInput
                  id="yourdata-phone"
                  type="tel"
                  placeholder={t('yourData.phonePlaceholder')}
                  value={data.phone_number}
                  onChange={e => onChange('phone_number', e.target.value.replace(/[^\d+ ]/g, ''))}
                  data-error={!!fieldErrors.phone_number}
                />
              </FormGroup>
            </FormGrid>

            <ButtonGroup>
              <SecondaryButton
                type="button"
                disabled={!changed || saving}
                onClick={reset}
              >
                <XIcon size={16} />
                {t('common.cancel')}
              </SecondaryButton>
              <PrimaryButton
                type="button"
                disabled={!changed || saving}
                onClick={save}
              >
                <CheckIcon size={16} />
                {saving ? t('yourData.saving') : t('common.save')}
              </PrimaryButton>
            </ButtonGroup>

          </FormSection>
        </ProfileSection>
      </PanelCard>
    </PanelContainer>
  );
}
